/*
 * Author: zoesvision.com
 * Purpose: Filter product photos and videos and control the scoped media gallery.
 */

if (!customElements.get('z-product-purchase')) {
  customElements.define(
    'z-product-purchase',
    class ZProductPurchase extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.addEventListener('click', this.onClick.bind(this), {
          signal: this.controller.signal,
        });

        const savedMode = this.closest('product-info')?.dataset.zMediaMode;
        this.setMode(savedMode || this.dataset.mode || 'photos');
        this.initThumbnailCarousel();
        this.initDescription();
        this.initButtonLabels();
        this.initBenefitsPlacement();
        this.initShareMenu();
        this.initImageViewer();
      }

      disconnectedCallback() {
        this.closeImageViewer();
        this.controller?.abort();
        this.descriptionObserver?.disconnect();
        this.buttonLabelObservers?.forEach((observer) => observer.disconnect());
        clearTimeout(this.copyLabelTimer);
        this.destroyThumbnailCarousel();
        this.restoreBenefitsPlacement();
        this.pauseMedia();
      }

      onClick(event) {
        const imageTrigger = event.target.closest('[data-image-open]');
        if (imageTrigger && this.contains(imageTrigger)) {
          this.openImageViewer(imageTrigger);
          return;
        }

        const shareTrigger = event.target.closest('[data-share-trigger]');
        if (shareTrigger && this.contains(shareTrigger)) {
          this.toggleShareMenu();
          return;
        }

        const copyShare = event.target.closest('[data-share-copy]');
        if (copyShare && this.contains(copyShare)) {
          this.copyShareLink(copyShare);
          return;
        }

        const shareLink = event.target.closest('[data-share-link]');
        if (shareLink && this.contains(shareLink)) {
          this.closeShareMenu();
          return;
        }

        const previousThumbnail = event.target.closest('[data-thumbnail-previous]');
        if (previousThumbnail && this.contains(previousThumbnail)) {
          this.thumbnailEmbla?.scrollPrev(this.reducedMotion?.matches);
          return;
        }

        const nextThumbnail = event.target.closest('[data-thumbnail-next]');
        if (nextThumbnail && this.contains(nextThumbnail)) {
          this.thumbnailEmbla?.scrollNext(this.reducedMotion?.matches);
          return;
        }

        const descriptionToggle = event.target.closest('[data-description-toggle]');
        if (descriptionToggle && this.contains(descriptionToggle)) {
          this.toggleDescription(descriptionToggle);
          return;
        }

        const modeButton = event.target.closest('[data-media-mode]');
        if (modeButton && this.contains(modeButton)) {
          this.setMode(modeButton.dataset.mediaMode);
          return;
        }

        const thumbnail = event.target.closest('[data-media-thumbnail]');
        if (thumbnail && this.contains(thumbnail) && !thumbnail.closest('[data-thumbnail-slide]')?.hidden) {
          this.selectMedia(thumbnail.dataset.mediaId);
        }
      }

      initImageViewer() {
        this.imageViewer = this.querySelector('[data-image-viewer]');
        if (!this.imageViewer) return;
        const signal = this.controller.signal;
        this.imageViewer.addEventListener('click', (event) => {
          if (event.target.closest('[data-image-close]') || event.target === this.imageViewer) {
            this.closeImageViewer();
          } else if (event.target.closest('[data-image-previous]')) {
            this.showViewerImage(this.viewerIndex - 1);
          } else if (event.target.closest('[data-image-next]')) {
            this.showViewerImage(this.viewerIndex + 1);
          }
        }, { signal });
        this.imageViewer.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.showViewerImage(this.viewerIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }, { signal });
        this.imageViewer.addEventListener('close', () => {
          this.releaseImageViewer();
        }, { signal });
      }

      openImageViewer(trigger) {
        if (!this.imageViewer || this.imageViewer.open) return;
        this.viewerImages = [...this.querySelectorAll('[data-image-open]')];
        const index = this.viewerImages.indexOf(trigger);
        if (index < 0) return;
        this.showViewerImage(index);
        this.imageViewer.showModal();
        document.documentElement.classList.add('z-product-image-viewer-open');
      }

      showViewerImage(index) {
        if (!this.viewerImages?.length) return;
        this.viewerIndex = (index + this.viewerImages.length) % this.viewerImages.length;
        const trigger = this.viewerImages[this.viewerIndex];
        const source = trigger.querySelector('img');
        const image = document.createElement('img');
        image.src = trigger.dataset.fullSrc;
        image.alt = source?.alt || '';
        image.width = source?.width || 1;
        image.height = source?.height || 1;
        image.decoding = 'async';
        this.imageViewer.querySelector('[data-image-viewer-content]').replaceChildren(image);
        this.imageViewer.querySelector('[data-image-counter]').textContent = `${this.viewerIndex + 1} / ${this.viewerImages.length}`;
        this.imageViewer.querySelectorAll('[data-image-previous], [data-image-next]').forEach((button) => {
          button.hidden = this.viewerImages.length < 2;
        });
        this.selectMedia(trigger.closest('[data-media-item]').dataset.mediaId);
      }

      closeImageViewer() {
        if (!this.imageViewer?.open) return;
        this.imageViewer.close();
        this.releaseImageViewer();
      }

      releaseImageViewer() {
        if (!document.querySelector('.z-product-purchase__lightbox[open]')) {
          document.documentElement.classList.remove('z-product-image-viewer-open');
        }
        if (this.isConnected && !this.imageViewer?.open) {
          this.viewerImages?.[this.viewerIndex]?.focus({ preventScroll: true });
        }
      }

      initShareMenu() {
        // zoesvision.com | 2026-08-19 | Purpose: Keep the configurable share popover isolated to this product section instance.
        this.share = this.querySelector('.z-product-purchase__share');
        this.shareTrigger = this.querySelector('[data-share-trigger]');
        this.shareMenu = this.querySelector('[data-share-menu]');
        if (!this.share || !this.shareTrigger || !this.shareMenu) return;

        document.addEventListener('pointerdown', (event) => {
          if (!this.shareMenu.hidden && !this.share.contains(event.target)) this.closeShareMenu();
        }, { signal: this.controller.signal });

        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || this.shareMenu.hidden) return;
          this.closeShareMenu();
          this.shareTrigger.focus();
        }, { signal: this.controller.signal });
      }

      toggleShareMenu() {
        if (!this.shareMenu) return;
        const shouldOpen = this.shareMenu.hidden;
        this.shareMenu.hidden = !shouldOpen;
        this.share?.classList.toggle('is-open', shouldOpen);
        this.shareTrigger?.setAttribute('aria-expanded', String(shouldOpen));
      }

      closeShareMenu() {
        if (!this.shareMenu) return;
        this.shareMenu.hidden = true;
        this.share?.classList.remove('is-open');
        this.shareTrigger?.setAttribute('aria-expanded', 'false');
      }

      async copyShareLink(button) {
        const url = button.dataset.shareCopy;
        if (!url) return;

        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
          } else {
            const textarea = document.createElement('textarea');
            textarea.value = url;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.append(textarea);
            textarea.select();
            document.execCommand('copy');
            textarea.remove();
          }

          const label = button.querySelector('[data-share-label]');
          if (label) {
            const originalLabel = label.textContent;
            label.textContent = button.dataset.copiedLabel || 'Copied';
            clearTimeout(this.copyLabelTimer);
            this.copyLabelTimer = setTimeout(() => {
              label.textContent = originalLabel;
            }, 1600);
          }
        } catch (error) {
          console.warn('z-product-purchase: unable to copy share link', error);
        }
      }

      initThumbnailCarousel() {
        this.thumbnailCarousel = this.querySelector('[data-thumbnail-carousel]');
        this.thumbnailViewport = this.querySelector('[data-thumbnail-viewport]');
        this.thumbnailPrevious = this.querySelector('[data-thumbnail-previous]');
        this.thumbnailNext = this.querySelector('[data-thumbnail-next]');
        this.thumbnailSlides = [...this.querySelectorAll('[data-thumbnail-slide]')];
        this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

        if (!this.thumbnailViewport || !this.thumbnailSlides.length) return;
        if (typeof globalThis.EmblaCarousel !== 'function') {
          this.thumbnailDependencyAttempts = (this.thumbnailDependencyAttempts || 0) + 1;
          if (this.thumbnailDependencyAttempts <= 120 && this.isConnected) {
            this.thumbnailDependencyFrame = requestAnimationFrame(() => this.initThumbnailCarousel());
          }
          return;
        }

        this.thumbnailDependencyAttempts = 0;
        this.thumbnailEmbla?.destroy();
        this.thumbnailEmbla = globalThis.EmblaCarousel(this.thumbnailViewport, {
          align: 'start',
          containScroll: 'trimSnaps',
          dragFree: true,
          loop: false,
          watchDrag: this.thumbnailSlides.filter((slide) => !slide.hidden).length > 1,
        });
        this.thumbnailEmbla.on('select', () => this.updateThumbnailControls());
        this.thumbnailEmbla.on('reInit', () => this.updateThumbnailControls());
        this.thumbnailCarousel?.classList.add('is-embla-ready');
        this.updateThumbnailControls();

        const selectedThumbnail = this.querySelector('[data-media-thumbnail][aria-current="true"]');
        if (selectedThumbnail) this.scrollThumbnailIntoView(selectedThumbnail.dataset.mediaId, true);
      }

      updateThumbnailControls() {
        if (this.thumbnailPrevious) this.thumbnailPrevious.disabled = !this.thumbnailEmbla?.canScrollPrev();
        if (this.thumbnailNext) this.thumbnailNext.disabled = !this.thumbnailEmbla?.canScrollNext();
      }

      reInitThumbnailCarousel() {
        cancelAnimationFrame(this.thumbnailReInitFrame);
        this.thumbnailReInitFrame = requestAnimationFrame(() => {
          this.thumbnailEmbla?.reInit({
            watchDrag: this.thumbnailSlides.filter((slide) => !slide.hidden).length > 1,
          });
          this.updateThumbnailControls();
        });
      }

      scrollThumbnailIntoView(mediaId, jump = this.reducedMotion?.matches) {
        if (!this.thumbnailEmbla) return;

        const visibleSlides = this.thumbnailSlides.filter((slide) => !slide.hidden);
        const selectedIndex = visibleSlides.findIndex(
          (slide) => slide.querySelector('[data-media-thumbnail]')?.dataset.mediaId === String(mediaId)
        );
        if (selectedIndex >= 0) this.thumbnailEmbla.scrollTo(selectedIndex, jump);
      }

      destroyThumbnailCarousel() {
        cancelAnimationFrame(this.thumbnailDependencyFrame);
        cancelAnimationFrame(this.thumbnailReInitFrame);
        this.thumbnailEmbla?.destroy();
        this.thumbnailCarousel?.classList.remove('is-embla-ready');
        this.thumbnailEmbla = null;
      }

      initDescription() {
        this.descriptionObserver?.disconnect();
        this.description = this.querySelector('[data-description]');
        this.descriptionToggle = this.querySelector('[data-description-toggle]');
        if (!this.description || !this.descriptionToggle) return;

        this.descriptionObserver = new ResizeObserver(() => this.updateDescriptionToggle());
        this.descriptionObserver.observe(this.description);
        requestAnimationFrame(() => this.updateDescriptionToggle());
      }

      updateDescriptionToggle() {
        if (!this.description || !this.descriptionToggle) return;

        const lineHeight = Number.parseFloat(getComputedStyle(this.description).lineHeight);
        const hasOverflow = this.description.scrollHeight > lineHeight * 2 + 1;
        this.descriptionToggle.hidden = !hasOverflow;

        if (!hasOverflow) {
          this.description.classList.remove('is-expanded');
          this.descriptionToggle.setAttribute('aria-expanded', 'false');
          this.descriptionToggle.textContent = this.descriptionToggle.dataset.moreLabel;
        }
      }

      toggleDescription(button) {
        if (!this.description) return;

        const isExpanded = this.description.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', String(isExpanded));
        button.textContent = isExpanded ? button.dataset.lessLabel : button.dataset.moreLabel;
      }

      initButtonLabels() {
        this.buttonLabelObservers?.forEach((observer) => observer.disconnect());
        this.buttonLabelObservers = [...this.querySelectorAll('[data-z-button-label]')].map((label) => {
          const observer = new MutationObserver(() => this.hydrateButtonLabel(label));
          observer.observe(label, { childList: true, subtree: true, characterData: true });
          return observer;
        });
      }

      hydrateButtonLabel(label) {
        if (label.querySelector('.z-button__text-layer')) return;

        const text = label.textContent.trim();
        label.setAttribute('aria-label', text);
        label.replaceChildren(...['primary', 'secondary'].map((layerName) => {
          const layer = document.createElement('span');
          layer.className = `z-button__text-layer z-button__text-layer--${layerName}`;
          layer.setAttribute('aria-hidden', 'true');

          [...text].forEach((character, index) => {
            const characterElement = document.createElement('span');
            // zoesvision.com | 2026-08-06 | Purpose: Preserve the shared original hover timing after dynamic label refreshes.
            characterElement.style.setProperty('--z-button-character-duration', `${index * 100 + 200}ms`);
            const characterText = character === ' ' ? '\u00a0' : character;
            if (layerName === 'secondary') {
              characterElement.dataset.character = characterText;
            } else {
              characterElement.textContent = characterText;
            }
            layer.append(characterElement);
          });

          return layer;
        }));
      }

      initBenefitsPlacement() {
        // zoesvision.com | 2026-08-14 | Purpose: Reuse one Theme Editor block tree while matching the mobile content order.
        this.benefits = this.querySelector('[data-benefits]');
        this.benefitsAnchor = this.querySelector('[data-benefits-anchor]');
        this.productInfo = this.querySelector('.z-product-purchase__info');
        if (!this.benefits || !this.benefitsAnchor || !this.productInfo) return;

        this.benefitsMediaQuery = matchMedia('(max-width: 749px)');
        this.benefitsMediaQuery.addEventListener('change', () => this.updateBenefitsPlacement(), {
          signal: this.controller.signal,
        });
        this.benefits.addEventListener('pointerdown', this.onBenefitsPointerDown.bind(this), {
          signal: this.controller.signal,
        });
        this.benefits.addEventListener('pointermove', this.onBenefitsPointerMove.bind(this), {
          signal: this.controller.signal,
        });
        this.benefits.addEventListener('pointerup', this.onBenefitsPointerUp.bind(this), {
          signal: this.controller.signal,
        });
        this.benefits.addEventListener('pointercancel', this.onBenefitsPointerUp.bind(this), {
          signal: this.controller.signal,
        });
        this.updateBenefitsPlacement();
      }

      onBenefitsPointerDown(event) {
        if (event.pointerType !== 'mouse' || this.benefits.scrollWidth <= this.benefits.clientWidth) return;

        this.benefitsDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          scrollLeft: this.benefits.scrollLeft,
        };
        this.benefitsDragTarget = this.benefits.scrollLeft;
        this.benefits.setPointerCapture(event.pointerId);
        this.benefits.classList.add('is-dragging');
      }

      onBenefitsPointerMove(event) {
        if (!this.benefitsDrag || event.pointerId !== this.benefitsDrag.pointerId) return;

        event.preventDefault();
        this.benefitsDragTarget = this.benefitsDrag.scrollLeft + this.benefitsDrag.startX - event.clientX;
        this.requestBenefitsDragFrame();
      }

      requestBenefitsDragFrame() {
        if (this.benefitsDragFrame) return;

        this.benefitsDragFrame = requestAnimationFrame(() => {
          this.benefitsDragFrame = null;
          const target = this.benefitsDragTarget;
          if (target == null) return;

          const distance = target - this.benefits.scrollLeft;
          this.benefits.scrollLeft += distance * 0.35;
          if (Math.abs(distance) > 0.5) {
            this.requestBenefitsDragFrame();
          } else {
            this.benefits.scrollLeft = target;
            this.benefitsDragTarget = null;
          }
        });
      }

      onBenefitsPointerUp(event) {
        if (!this.benefitsDrag || event.pointerId !== this.benefitsDrag.pointerId) return;

        this.benefitsDrag = null;
        this.benefits.classList.remove('is-dragging');
      }

      updateBenefitsPlacement() {
        if (!this.benefits || !this.benefitsAnchor || !this.productInfo) return;

        if (this.benefitsMediaQuery.matches) {
          this.productInfo.after(this.benefits);
        } else {
          this.benefitsAnchor.after(this.benefits);
        }
      }

      restoreBenefitsPlacement() {
        cancelAnimationFrame(this.benefitsDragFrame);
        this.benefitsDragFrame = null;
        this.benefitsDragTarget = null;
        this.benefitsDrag = null;
        this.benefits?.classList.remove('is-dragging');
        if (this.benefits && this.benefitsAnchor) this.benefitsAnchor.after(this.benefits);
      }

      setMode(requestedMode) {
        const items = [...this.querySelectorAll('[data-media-item]')];
        const thumbnails = [...this.querySelectorAll('[data-media-thumbnail]')];
        const availableModes = new Set(items.map((item) => item.dataset.mediaKind));
        let mode = requestedMode === 'video' ? 'video' : 'photos';

        if (!availableModes.has(mode)) {
          mode = availableModes.has('photos') ? 'photos' : 'video';
        }

        this.dataset.mode = mode;
        const productInfo = this.closest('product-info');
        if (productInfo) productInfo.dataset.zMediaMode = mode;

        this.querySelectorAll('[data-media-mode]').forEach((button) => {
          const isActive = button.dataset.mediaMode === mode;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', String(isActive));
        });

        items.forEach((item) => {
          item.hidden = item.dataset.mediaKind !== mode;
        });

        thumbnails.forEach((thumbnail) => {
          const slide = thumbnail.closest('[data-thumbnail-slide]');
          if (slide) slide.hidden = thumbnail.dataset.mediaKind !== mode;
        });

        const activeItem = items.find((item) => item.classList.contains('is-active') && !item.hidden);
        const firstVisible = items.find((item) => !item.hidden);
        if (!activeItem && firstVisible) {
          this.selectMedia(firstVisible.dataset.mediaId);
        } else {
          this.syncVideoPreload(items, activeItem);
          this.pauseMedia(activeItem);
        }
        this.reInitThumbnailCarousel();
      }

      selectMedia(mediaId) {
        const items = [...this.querySelectorAll('[data-media-item]')];
        const target = items.find((item) => item.dataset.mediaId === String(mediaId) && !item.hidden);
        if (!target) return;
        this.syncVideoPreload(items, target);

        items.forEach((item) => {
          item.classList.toggle('is-active', item === target);
        });

        this.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
          thumbnail.setAttribute('aria-current', String(thumbnail.dataset.mediaId === String(mediaId)));
        });

        this.scrollThumbnailIntoView(mediaId);
        this.pauseMedia(target);
      }

      // zoesvision.com | 2026-08-19 | Purpose: Warm selected and next product videos on initial load and later gallery changes.
      syncVideoPreload(items, target) {
        if (!target) return;
        const visibleItems = items.filter((item) => !item.hidden);
        const selectedIndex = visibleItems.indexOf(target);
        const nextItem = visibleItems[selectedIndex + 1];
        items.forEach((item) => {
          item.querySelectorAll('video').forEach((video) => {
            video.preload = item === target || item === nextItem ? 'metadata' : 'none';
          });
        });
      }

      setActiveMedia(mediaId) {
        const target = this.querySelector(`[data-media-item][data-media-id="${CSS.escape(String(mediaId))}"]`);
        if (!target) return;
        this.setMode(target.dataset.mediaKind);
        this.selectMedia(mediaId);
      }

      pauseMedia(activeItem = null) {
        this.querySelectorAll('[data-media-item]').forEach((item) => {
          if (item === activeItem) return;

          item.querySelectorAll('video').forEach((video) => video.pause());
          item.querySelectorAll('iframe').forEach((iframe) => {
            if (!iframe.src) return;
            const source = iframe.dataset.zSource || iframe.src;
            iframe.dataset.zSource = source;
            iframe.src = source;
          });
        });
      }
    }
  );
}
